// Field validation rule operators
export type OperatorType =
  | "equals"
  | "notEquals"
  | "isEmpty"
  | "isNotEmpty"
  | "greaterThan"
  | "lessThan"
  | "greaterThanOrEqual"
  | "lessThanOrEqual"
  | "contains"
  | "notContains"
  | "startsWith"
  | "endsWith"
  | "matches"
  | "notMatches"
  | "required"

export type FieldInputType =
  | "text"
  | "number"
  | "date"
  | "datetime"
  | "textarea"
  | "radio"
  | "checkbox"
  | "selector"
  | "typography"
  | "boolean"
  | "search"
  | "party"

export type FieldDataType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "datetime"
  | "email"
  | "object"
  | "array"
  | "function"
  // Merged DfeConfig field types
  | "text"
  | "textArray"
  | "dateTime"
  | "entityArray"

export type BlockType =
  | "regular"
  | "table"
  | "product"
  | "packaging"
  | "declaration"
  | "defaultDeclaration"
  | "classifications"

export const PACKAGING_BLOCK_TYPE: BlockType = "packaging"

export type RowType = "regular" | "statement"

export type CellType =
  | "typography"
  | "link"
  | "values"
  | "textInput"
  | "textarea"
  | "checkbox"
  | "selector"

export type StatementInputType =
  | "companyAddress"
  | "companyName"
  | "defaultTracesStatement"
  | "manufacturerName"
  | "overseasMarket"
  | "sourceED"
  | "sourceEDDOI"
  | "user"

export type DeclarationGroupType =
  | "PHRASE"
  | "MANDATORY"
  | "MANDATORY_EXACTLY_ONE"
  | "MANDATORY_ONE_OR_MORE"
  | "OPTIONAL_ZERO_OR_ONE"
  | "OPTIONAL_ZERO_OR_MORE"
  | "FOOTNOTE"

export enum DeclarationGroupTypeEnum {
  PHRASE = "PHRASE",
  MANDATORY = "MANDATORY",
  MANDATORY_EXACTLY_ONE = "MANDATORY_EXACTLY_ONE",
  MANDATORY_ONE_OR_MORE = "MANDATORY_ONE_OR_MORE",
  OPTIONAL_ZERO_OR_ONE = "OPTIONAL_ZERO_OR_ONE",
  OPTIONAL_ZERO_OR_MORE = "OPTIONAL_ZERO_OR_MORE",
  FOOTNOTE = "FOOTNOTE",
}
export interface Condition {
  fieldRef: string
  operator: OperatorType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any
}

export interface ConditionRule {
  if?: Condition
  then?: Condition
}

export interface StringValidationRules {
  required?: boolean
  maxLength?: number
  minLength?: number
  regex?: string
  email?: boolean
}
export interface NumberValidationRules {
  required?: boolean
  min?: number
  max?: number
}
export interface DateValidationRules {
  required?: boolean
  min?: number
  max?: number
}

export interface ArrayValidationRules {
  required?: boolean
  minLength?: number
  maxLength?: number
  uniqueItems?: boolean
}
// Validation rules and messages
export interface ValidationRules
  extends StringValidationRules,
    NumberValidationRules,
    DateValidationRules {
  custom?: ConditionRule
  // Merged DfeConfig validation rules
  length?: number
  greaterThan?: number
  lessThan?: number
  pattern?: string
}

export interface ValidationMessages {
  required?: string
  maxLength?: string
  minLength?: string
  max?: string
  min?: string
  range?: string
  email?: string
  regex?: string
  uniqueItems?: string
  custom?: string
  // Merged DfeConfig validation messages
  greaterThan?: string
  lessThan?: string
  pattern?: string
}

export interface FieldValidation {
  rules: ValidationRules
  messages: ValidationMessages
}

// Field definition
export interface Field {
  fuid: string
  dataType: FieldDataType
  label?: string
  parent?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any
  validation?: FieldValidation
  category?: string
  classificationCode?: string
  customLookup?: CustomLookupType
}

export interface LabelValue {
  label: string
  value: string
}

export interface SearchOption {
  collectionName: string
  queryAttributes?: string
  valueAttribute?: string
  displayAttr?: string
  orderBy?: string
  precondition?: Condition[]
  multiple?: boolean
  freeSolo?: boolean
  filter_by?: string
  dependOn?: string
  dependencyFilterKey?: string
  allowBulkEntry?: boolean
  bulkEntryErrorMessage?: string
  includeNotApplicable?: boolean
}

export interface NumberInputOption {
  allowNegative?: boolean
  decimalScale?: number
  min?: number
  max?: number
}

// Table column definition
export interface TableColumn {
  label: string
  type: CellType
  fieldRef: string[]
}

// Field in a row
export enum CustomLookupType {
  None = "NONE",
  EuOperatorSearch = "EU_OPERATOR_SEARCH",
}

export interface FieldInput {
  fieldRef: string
  inputType: FieldInputType
  name?: string
  placeholder?: string
  readOnly?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any
  options?: LabelValue[]
  columns?: TableColumn
  searchOption?: SearchOption
  numberInputOption?: NumberInputOption
  columnsToDisplay?: "CODE" | "TEXT" | "CODE_AND_TEXT"
  searchable?: boolean
  customLookup?: CustomLookupType
}

// Row structure
export interface Row {
  label?: string
  type?: RowType
  template?: string
  description?: string
  required?: boolean
  fields?: FieldInput[]
  hide?: Condition
  columns?: TableColumn
}

// Block structure (regular block or table block)
export interface Block {
  id?: string
  title?: string
  description?: string
  emptyMessage?: string
  blockType?: BlockType
  rows?: Row[]
  columns?: TableColumn[]
  hide?: Condition
  actions?: Action[]
  groupType?: DeclarationGroupTypeEnum
}
export interface StatementInput extends FieldInput {
  inputSequenceNumber: number
  dataType: StatementInputType
  size: number
}
export interface Statement {
  statementDisplayCode: string // `${templateCode}.${majorVerison}/${statementCode}${signatorySuffix}`
  statementCode: number
  statementText: string
  statementNumber?: string
  inputs: StatementInput[]
  translations?: Record<string, string> // { "zh": "中文文本", "ja": "日本語テキスト" }
}
export type DeclarationGroupNumbering =
  | "NUMBER"
  | "LOWER_ALPHA"
  | "UPPER_ALPHA"
  | "LOWER_ROMAN"
  | "UPPER_ROMAN"
  | "CUSTOM"
  | "BULLET"
  | "NONE"

export enum DeclarationGroupNumberingEnum {
  NUMBER = "NUMBER",
  LOWER_ALPHA = "LOWER_ALPHA",
  UPPER_ALPHA = "UPPER_ALPHA",
  LOWER_ROMAN = "LOWER_ROMAN",
  UPPER_ROMAN = "UPPER_ROMAN",
  CUSTOM = "CUSTOM",
  BULLET = "BULLET",
  NONE = "NONE",
}

export interface DeclarationGroup {
  declarationGroupType: DeclarationGroupTypeEnum
  blockType: BlockType
  indent: number
  numbering: DeclarationGroupNumbering
  startAt: number
  statements: Statement[]
}

export interface Action {
  id: string
  label?: string
  actionType?: string
  steps?: string[]
}

// Section structure
export interface Section {
  id: string
  title?: string
  description?: string
  children: Block[]
  actions?: Action[]
}

// Layout structure
export interface Layout {
  sections: Section[]
}

export interface Layouts {
  requests: Layout
  products: Layout
}
// Config JSON structure
export interface ConfigJSONBase {
  templateId?: string
  templateRevisionId?: string
}
export interface ConfigJSON extends ConfigJSONBase {
  meta: {
    // ConfigJSON meta fields
    description?: string
    version?: string
    author?: string
    date?: string
    catalogue?: string[]
    // Merged DfeConfig meta fields
    code?: string
    title?: string
    majorVersion?: number
    minorVersion?: number
    effective?: string
    expire?: string
    submissionLayout?: SubmissionLayoutType
    certificateType?: string
  }
  fields: Field[]
  requests?: Layout
  products?: Layout
  review?: Layout
  declarationGroups?: DeclarationGroup[]
  crossFieldValidation?: CrossFieldValidationConfig
  translations?: {
    templateTitle: { lang: string; text: string }[]
    fieldCaptions: {
      fuid: string
      translations: { lang: string; text: string }[]
    }[]
  }
}

export interface FieldDisableGroupConfig {
  whenActive: string[]
  disable: string[]
  message?: string
}

export interface ExclusiveGroupConfig {
  fieldAValue: string
  fieldAUnit: string
  fieldBValue: string
  fieldBUnit: string
  message: string
}

export interface CrossExclusionConfig {
  fieldA: string
  fieldB: string
  message: string
}

export interface ValueUnitPairConfig {
  value: string
  unit: string
}

export interface CrossFieldValidationConfig {
  fieldDisableGroups: FieldDisableGroupConfig[]
  exclusiveGroups: ExclusiveGroupConfig[]
  crossExclusions: CrossExclusionConfig[]
  valueUnitPairs: ValueUnitPairConfig[]
}

enum SubmissionLayoutType {
  ApExport = "AP_EXPORT",
  ApTransfer = "AP_TRANSFER",
  Custom = "CUSTOM",
  PhytoExport = "PHYTO_EXPORT",
}

export enum OperatorTypeEnum {
  EQUALS = "equals",
  NOT_EQUALS = "notEquals",
  IS_EMPTY = "isEmpty",
  IS_NOT_EMPTY = "isNotEmpty",
  GREATER_THAN = "greaterThan",
  LESS_THAN = "lessThan",
  GREATER_THAN_OR_EQUAL = "greaterThanOrEqual",
  LESS_THAN_OR_EQUAL = "lessThanOrEqual",
  CONTAINS = "contains",
  NOT_CONTAINS = "notContains",
  STARTS_WITH = "startsWith",
  ENDS_WITH = "endsWith",
  MATCHES = "matches",
  NOT_MATCHES = "notMatches",
  REQUIRED = "required",
}

export enum InputType {
  TEXT = "text",
  NUMBER = "number",
  EMAIL = "email",
  TEXTAREA = "textarea",
  DATE = "date",
  DATETIME = "datetime",
  TIME = "time",
  BOOLEAN = "boolean",
  SELECT = "selector",
  RADIO = "radio",
  CHECKBOX = "checkbox",
  SEARCH = "search",
  PARTY = "party",
}

export enum FieldDataTypeEnum {
  STRING = "string",
  NUMBER = "number",
  BOOLEAN = "boolean",
  DATE = "date",
  DATETIME = "datetime",
  EMAIL = "email",
  OBJECT = "object",
  ARRAY = "array",
  FUNCTION = "function",
}

export enum CellTypeEnum {
  TYPOGRAPHY = "typography",
  LINK = "link",
  VALUES = "values",
  TEXT_INPUT = "textInput",
  CHECKBOX = "checkbox",
  SELECTOR = "selector",
}

export enum RowTypeEnum {
  REGULAR = "regular",
  STATEMENT = "statement",
}

export type RecipeFieldOverride = Partial<{
  label?: string
  dataType?: FieldDataType
  inputType?: FieldInputType | string
  order?: number[]
  maxLength?: number
  mandatory?: boolean
  readOnly?: boolean
  category?: string
  classificationCode?: string
  searchOption?: SearchOption
  numberInputOption?: NumberInputOption
  selectorOptions?: LabelValue[]
  validation?: FieldValidation
  searchable?: boolean
}>

export type RecipeOverrideInput = Array<{
  fuid: string
  override: RecipeFieldOverride
}>
